import React, { useEffect, useState } from "react";
import api from "../../api/api";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import { TextField, Autocomplete } from "@mui/material";

const PaymentRequestView = ({ onUpdateDetails }) => {
  const [open, setOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dateOptions, setDateOptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  const token = localStorage.getItem("access");
  const payload = token ? JSON.parse(atob(token.split(".")[1])) : {};
  const userType = payload.user_type || ""; // "fiscal", "principal", "admin"

  const fetchRequests = async () => {
    try {
      const res = await api.get("/payment-requests/", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.length > 0) {
        const sortedRequests = res.data.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setRequests(sortedRequests);

        let filtered = sortedRequests;

        // Filter based on user_type so completed requests for this user are hidden
        if (userType === "fiscal") {
          filtered = filtered.filter((r) => !r.fiscal_signature_url);
        } else if (userType === "principal") {
          filtered = filtered.filter((r) => !r.principal_signature_url);
        } else if (userType === "admin") {
          filtered = filtered.filter((r) => !r.attested_signature_url);
        }

        // Filter by selected date
        if (selectedDate) {
          filtered = filtered.filter(
            (r) => new Date(r.date).toISOString().split("T")[0] === selectedDate
          );
        }

        setFilteredRequests(filtered);
        setCurrentIndex(0);

        const uniqueDates = Array.from(
          new Set(
            sortedRequests.map((r) =>
              new Date(r.date).toISOString().split("T")[0]
            )
          )
        ).sort((a, b) => new Date(b) - new Date(a));
        setDateOptions(uniqueDates);

        setOpen(filtered.length > 0);
      } else {
        setOpen(false);
      }
    } catch (err) {
      console.error("Failed to fetch requests:", err);
    }
  };

  useEffect(() => {
    fetchRequests();
    const interval = setInterval(fetchRequests, 30000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const handleAction = async (id, action) => {
    if (["approve", "attest", "sign"].includes(action)) {
      const result = await Swal.fire({
        title: `Are you sure you want to ${action.replace("_", " ")}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      });
      if (!result.isConfirmed) return;
    }

    try {
      const res = await api.post(
        `/payment-requests/${id}/${action}/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      Swal.fire(
        "Success",
        `Payment request ${action.replace("_", " ")} successfully!`,
        "success"
      );

      const updatedItem = res.data ?? null;

      const updatedRequests = requests.map((req) =>
        req.id === id ? (updatedItem ? updatedItem : req) : req
      );
      setRequests(updatedRequests);

      let updatedFiltered;
      if (updatedItem) {
        updatedFiltered = filteredRequests.map((req) =>
          req.id === id ? updatedItem : req
        );
      } else {
        updatedFiltered = filteredRequests.filter((req) => req.id !== id);
      }

      // After action, filter out completed requests for this user_type
      if (userType === "fiscal") {
        updatedFiltered = updatedFiltered.filter((r) => !r.fiscal_signature_url);
      } else if (userType === "principal") {
        updatedFiltered = updatedFiltered.filter((r) => !r.principal_signature_url);
      } else if (userType === "admin") {
        updatedFiltered = updatedFiltered.filter((r) => !r.attested_signature_url);
      }

      setFilteredRequests(updatedFiltered);

      if (updatedFiltered.length === 0) setOpen(false);
      else if (currentIndex >= updatedFiltered.length)
        setCurrentIndex(updatedFiltered.length - 1);

      if (onUpdateDetails)
        onUpdateDetails(updatedRequests.find((r) => r.id === id));
    } catch (err) {
      const message = err.response?.data?.error || "Something went wrong!";
      Swal.fire("Error", message, "error");
    }
  };

  const handleNext = () => {
    if (currentIndex < filteredRequests.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const handlePrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleCancel = () => setOpen(false);

  const handleDateFilter = (event, value) => {
    setSelectedDate(value);
    fetchRequests();
  };

  const currentRequest = filteredRequests[currentIndex];

  return (
    <AnimatePresence>
      {open && currentRequest && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: "1rem",
          }}
        >
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ duration: 0.4 }}
            style={{
              background: "linear-gradient(145deg, #f0f0f3, #d9d9d9)",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "700px",
              maxHeight: "85vh",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0,0,0,0.25)",
              display: "flex",
              flexDirection: "column",
              padding: "2rem",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <h2 style={{ color: "#333" }}>Pending Payment Requests</h2>
              <Autocomplete
                options={dateOptions}
                value={selectedDate}
                onChange={handleDateFilter}
                sx={{ width: 180 }}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Date" size="small" />
                )}
              />
            </div>

            <motion.div
              key={currentRequest.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              style={{
                background: "#fff",
                borderRadius: "15px",
                padding: "1.5rem",
                boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "0.8rem",
              }}
            >
              <p>
                <strong>Submitted by:</strong> {currentRequest.created_by.username}
              </p>
              <p>
                <strong>Amount:</strong> {currentRequest.amount_in_words}
              </p>
              <p>
                <strong>Payment Method:</strong> {currentRequest.payment_method}
              </p>
              <p>
                <strong>Details:</strong> {currentRequest.payment_details}
              </p>
              <p>
                <strong>Submitted On:</strong>{" "}
                {new Date(currentRequest.date).toLocaleString()}
              </p>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "1rem",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  padding: "1rem",
                }}
              >
                <p style={{ flex: "1 1 200px", margin: 0 }}>
                  <strong>Fiscal Affairs Signed:</strong>{" "}
                  {currentRequest.fiscal_signature_url ? "Yes" : "No"}
                </p>
                <p style={{ flex: "1 1 200px", margin: 0 }}>
                  <strong>Principal Approved:</strong>{" "}
                  {currentRequest.principal_signature_url ? "Yes" : "No"}
                </p>
                <p style={{ flex: "1 1 200px", margin: 0 }}>
                  <strong>Gen. OverSeer Attested:</strong>{" "}
                  {currentRequest.attested_signature_url ? "Yes" : "No"}
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  flexWrap: "wrap",
                  marginTop: "1rem",
                }}
              >
                <ActionButton
                  color="#4caf50"
                  label="Approve (Principal)"
                  onClick={() => handleAction(currentRequest.id, "approve")}
                />
                <ActionButton
                  color="#ff4d4f"
                  label="Disapprove"
                  onClick={() => handleAction(currentRequest.id, "disapprove")}
                />
                <ActionButton
                  color="#ff9800"
                  label="Remind Later"
                  onClick={() => handleAction(currentRequest.id, "remind_later")}
                />
                <ActionButton
                  color="#333"
                  label="Attest (Admin)"
                  onClick={() => handleAction(currentRequest.id, "attest")}
                />
                <ActionButton
                  color="#0066cc"
                  label="Sign (Fiscal Affairs)"
                  onClick={() => handleAction(currentRequest.id, "sign")}
                />
                <ActionButton
                  color="transparent"
                  label="Never Show"
                  textColor="#333"
                  border="2px solid #333"
                  onClick={() => handleAction(currentRequest.id, "never_show")}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "1rem",
                }}
              >
                <ActionButton
                  color="#ccc"
                  textColor="#333"
                  label="Previous"
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                />
                <ActionButton color="#000" label="Cancel" onClick={handleCancel} />
                <ActionButton
                  color="#ccc"
                  textColor="#333"
                  label="Next"
                  onClick={handleNext}
                  disabled={currentIndex === filteredRequests.length - 1}
                />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ActionButton = ({
  color,
  textColor = "#fff",
  border = "none",
  label,
  onClick,
  disabled,
}) => (
  <motion.button
    whileHover={{ scale: disabled ? 1 : 1.05 }}
    whileTap={{ scale: disabled ? 1 : 0.95 }}
    onClick={onClick}
    disabled={disabled}
    style={{
      background: color,
      color: textColor,
      border,
      padding: "0.6rem 1.2rem",
      borderRadius: "12px",
      cursor: disabled ? "not-allowed" : "pointer",
      fontWeight: 600,
      fontSize: "1rem",
      margin: "0.3rem",
      boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
      transition: "all 0.3s ease",
      opacity: disabled ? 0.6 : 1,
    }}
  >
    {label}
  </motion.button>
);

export default PaymentRequestView;
